import {
    axiosDelete,
    get,
    getFile,
    post,
    put,
} from '../utility/Requests';
import {
    type FederatedPolicyPagedResult,
    Product,
    type TenantPolicyEntry,
    type TenantPolicyResponse,
} from './GovernancePolicy.types';
import type {
    ApiFlavor,
    ByoLlmCompatibilityProbeSummary,
    ByoLlmConfiguration,
    ByoOperationGroupConfiguration,
    ByoProductLlmConfiguration,
    ConfigureModelResponse,
    CustomRoutingRequest,
    LlmConnectorType,
    ModelImpactFilters,
    ModelImpactProcessesResponse,
    ModelImpactStatus,
    OrganizationModelHubConfiguration,
    RoutingRuleHistoryEntry,
    TenantModelHubConfiguration,
} from './LlmGateway.types';

export function getByoProductConfigurations(organizationId: string, tenantId: string) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byom/products`;
    return get<Record<string, Record<string, ByoOperationGroupConfiguration>>>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function getByoProductLlmConfigurations(organizationId: string, tenantId: string, includeConnectionDetails: boolean = false) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations?includeConnectionDetails=${includeConnectionDetails}`;
    return get<ByoProductLlmConfiguration[]>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function getByoProductLlmConfiguration(organizationId: string, tenantId: string, configurationId: string, forceRefreshConnectionDetails: boolean = false) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations/${configurationId}?forceRefreshConnectionDetails=${forceRefreshConnectionDetails}`;
    return get<ByoProductLlmConfiguration>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function addByoProductLlmConfiguration(organizationId: string, tenantId: string, configuration: Partial<ByoProductLlmConfiguration>) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations`;
    return post<ByoProductLlmConfiguration>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
        body: configuration,
    });
}

export function updateByoProductLlmConfiguration(organizationId: string, tenantId: string, configurationId: string, configuration: Partial<ByoProductLlmConfiguration>) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations/${configurationId}`;
    return put<void>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
        body: configuration,
    });
}

export function deleteByoProductLlmConfiguration(organizationId: string, tenantId: string, configurationId: string) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations/${configurationId}`;
    return axiosDelete(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function validateByoProductLlmConfiguration(
    organizationId: string,
    tenantId: string,
    configuration: Partial<ByoProductLlmConfiguration>,
    configurationMode?: string
) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byo/product/llm-configurations/validate`;
    const headers: Record<string, string> = { extendRequestHeaders: 'true' };

    if (configurationMode) {
        headers['X-UiPath-LlmGateway-ByoConfigMode'] = configurationMode;
    }

    return post<Record<string, ByoLlmCompatibilityProbeSummary>>(url, {
        ignoreBasePath: true,
        headers,
        body: configuration,
    });
}

export function getAvailableModelsForByoLlmConfiguration(organizationId: string, tenantId: string, configuration: Partial<ByoLlmConfiguration>) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byom/available-models`;
    return post<string[]>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
        body: configuration,
    });
}

export function getVendorToLlmsMappings(organizationId: string, tenantId: string) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byom/vendors/llms`;
    return get<Record<LlmConnectorType, string[]>>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function getVendorLlmApiFlavors(organizationId: string, tenantId: string) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/byom/vendors/llms/api-flavors`;
    return get<Record<string, ApiFlavor[] | null>>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function getModelHubTenantOverview(organizationId: string, tenantId: string, forceRefreshPolicy: boolean = false) {
    const query = forceRefreshPolicy ? '?forceRefreshPolicy=true' : '';
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/model-hub/tenant-overview${query}`;
    return get<TenantModelHubConfiguration>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function downloadModelHubTenantOverview(organizationId: string, tenantId: string) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/model-hub/tenant-overview/export`;
    return getFile(url, {
        ignoreBasePath: true,
        headers: {
            Accept: 'text/csv',
            extendRequestHeaders: 'true',
        },
    });
}

export function getModelHubPreviewPolicy(organizationId: string, tenantId: string, policyId: string | null) {
    const query = policyId == null ? '' : `?policyId=${encodeURIComponent(policyId)}`;
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/model-hub/preview-policy${query}`;
    return get<TenantModelHubConfiguration>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function getModelHubOrganizationOverview(organizationId: string, tenantId: string) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/model-hub/organization-overview`;
    return get<OrganizationModelHubConfiguration>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function getModelHubViewHistory(
    organizationId: string,
    tenantId: string,
    modelHubProduct: string,
    feature: string,
    model: string,
) {
    const params = `modelHubProduct=${encodeURIComponent(modelHubProduct)}&feature=${encodeURIComponent(feature)}&model=${encodeURIComponent(model)}`;
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/model-hub/view-history?${params}`;
    return get<RoutingRuleHistoryEntry[]>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

/**
 * Serializes the Model Impact filters. `product`/`feature` are omitted when empty so the gateway
 * treats them as "all" rather than as an empty-string match.
 */
function buildModelImpactParams({
    model, product, feature, period,
}: ModelImpactFilters) {
    const params = new URLSearchParams({
        model,
        // A whole number of days. The unit is fixed, so the gateway binds it as an int rather than
        // parsing a suffix.
        period: String(period),
    });

    if (product) {
        params.set('product', product);
    }

    if (feature) {
        params.set('feature', feature);
    }

    return params;
}

/**
 * Readiness of the tenant's snapshot; polled while it is still building. Scoped by the organization
 * and tenant in the path and nothing else — the snapshot covers the whole tenant, so it does not
 * vary by model, product, feature or period.
 */
export function getModelImpactStatus(organizationId: string, tenantId: string) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/model-hub/impact/status`;
    return get<ModelImpactStatus>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

/**
 * One page of impacted processes. `nextEntry` is the cursor returned by the previous page.
 * Returns whatever the snapshot holds so far, so partial results are available while it is Pending.
 */
export function getModelImpactProcesses(
    organizationId: string,
    tenantId: string,
    filters: ModelImpactFilters,
    options?: {
        nextEntry?: string;
        forceRefresh?: boolean;
    },
) {
    const params = buildModelImpactParams(filters);

    if (options?.nextEntry) {
        params.set('nextEntry', options.nextEntry);
    }

    if (options?.forceRefresh) {
        params.set('forceRefresh', 'true');
    }

    const url = `/${organizationId}/${tenantId}/llmgateway_/api/model-hub/impact/processes?${params}`;
    return get<ModelImpactProcessesResponse>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function getModelHubCustomRouting(
    organizationId: string,
    tenantId: string,
    modelHubProduct: string,
    feature: string,
    primaryModel: string,
    currentModel: string,
) {
    const params = new URLSearchParams({
        organizationId,
        tenantId,
        modelHubProduct,
        feature,
        primaryModel,
        currentModel,
    });
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/model-hub/custom-routing?${params.toString()}`;
    return get<ConfigureModelResponse>(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function saveModelHubCustomRouting(
    organizationId: string,
    tenantId: string,
    request: CustomRoutingRequest,
) {
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/model-hub/custom-routing`;
    return post(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
        body: request,
    });
}

export function getFederatedPolicies() {
    const url = `/roboticsops_/api/Policy/federated/paged?pageSize=50&pageIndex=0&productNames=${Product.AITL}`;
    return get<FederatedPolicyPagedResult>(url, {
        stripPortalPath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function getTenantPolicies(tenantId: string) {
    const url = `/roboticsops_/api/Tenant/${tenantId}`;
    return get<TenantPolicyResponse>(url, {
        stripPortalPath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}

export function updateTenantPolicies(tenantPolicies: TenantPolicyEntry[]) {
    const url = '/roboticsops_/api/Tenant/';
    return post(url, {
        stripPortalPath: true,
        headers: { extendRequestHeaders: 'true' },
        body: tenantPolicies,
    });
}

export function deleteModelHubCustomRouting(
    organizationId: string,
    tenantId: string,
    product: string,
    feature: string,
    ruleId: string,
) {
    const params = `product=${encodeURIComponent(product)}&feature=${encodeURIComponent(feature)}&id=${encodeURIComponent(ruleId)}`;
    const url = `/${organizationId}/${tenantId}/llmgateway_/api/model-hub/custom-routing?${params}`;
    return axiosDelete(url, {
        ignoreBasePath: true,
        headers: { extendRequestHeaders: 'true' },
    });
}
